import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { messages, type ListSummary } from '@nesc/shared';
import { api, errorMessage, type SingleResponse } from '@/lib/api-client';
import { qk } from '@/lib/query-client';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { Input, Textarea } from '@/components/ui/input';

/**
 * リストの作成（空の静的リスト）と、名前・説明の編集。
 * 企業の出し入れはリスト詳細と企業一覧で行う。
 */
export const ListFormDialog = ({
  open,
  onOpenChange,
  list,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 指定があれば編集、なければ新規作成 */
  list?: ListSummary | null;
  onSaved?: (list: ListSummary) => void;
}) => {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  useEffect(() => {
    if (open) {
      setName(list?.name ?? '');
      setDescription(list?.description ?? '');
    }
  }, [open, list]);

  const save = useMutation({
    mutationFn: () => {
      const body = { name: name.trim(), description: description.trim() || undefined };
      return list
        ? api.patch<SingleResponse<ListSummary>>(`/lists/${list.id}`, body)
        : api.post<SingleResponse<ListSummary>>('/lists', { ...body, type: 'static' });
    },
    onSuccess: (result) => {
      toast.success(list ? '保存しました' : `「${result.data.name}」を作成しました`);
      void queryClient.invalidateQueries({ queryKey: qk.lists });
      onOpenChange(false);
      onSaved?.(result.data);
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const blockedReason = !name.trim() ? 'リスト名を入力してください' : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{list ? messages.list.edit : messages.list.create}</DialogTitle>
          <DialogDescription>
            {list
              ? '名前と説明を変更します。'
              : '空の静的リストを作ります。企業は企業一覧で選んで追加します。'}
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-4">
          <Field label="リスト名" required>
            {(props) => (
              <Input
                {...props}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="医療SaaS_規模適合"
                maxLength={80}
              />
            )}
          </Field>
          <Field label="説明" hint="用途や絞り込みの意図など">
            {(props) => (
              <Textarea
                {...props}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                maxLength={500}
              />
            )}
          </Field>
        </DialogBody>

        <DialogFooter>
          <Button variant="secondary" size="lg" onClick={() => onOpenChange(false)}>
            {messages.common.cancel}
          </Button>
          <Button
            size="lg"
            onClick={() => save.mutate()}
            disabled={save.isPending || !!blockedReason}
            disabledReason={blockedReason ?? undefined}
          >
            {list ? messages.common.save : messages.list.create}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
